import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { SPILLER_NAVN_MAX_LENGDE } from '@/data/types';

const SHEET_WIDTH = 980;
const SHEET_HEIGHT = 880;
// Runde 45: bevisst avvik fra MODAL_PADDING (32). NavnInputModal har
// et stort DOM-input som krever ekstra luft rundt seg for å føles
// romslig på små skjermer. Dokumentert i config.ts ved konstantens
// definisjon ("Avvik dokumenteres lokalt").
const PADDING = 40;

export class NavnInputModal {
  private layer?: Phaser.GameObjects.Container;
  private domWrapper?: HTMLDivElement;
  private input?: HTMLInputElement;
  private onFerdigCb?: (navn: string) => void;
  private knappBg?: Phaser.GameObjects.Rectangle;
  private knappTxt?: Phaser.GameObjects.Text;
  private feilTekst?: Phaser.GameObjects.Text;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(onFerdig: (navn: string) => void): void {
    if (this.layer) return;
    this.onFerdigCb = onFerdig;

    this.layer = this.scene.add.container(0, 0).setDepth(400);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.82)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;

    const ramme = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, SHEET_HEIGHT, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(ramme);

    const innerPad = 14;
    const sheet = this.scene.add
      .rectangle(
        sheetX + innerPad,
        sheetY + innerPad,
        SHEET_WIDTH - innerPad * 2,
        SHEET_HEIGHT - innerPad * 2,
        COLORS.beige,
        1,
      )
      .setOrigin(0, 0);
    this.layer.add(sheet);

    const contentX = sheetX + PADDING;
    const contentW = SHEET_WIDTH - PADDING * 2;
    let y = sheetY + PADDING + 8;

    const ikon = this.scene.add
      .text(contentX, y, '✎', {
        fontFamily: FONTS.display,
        fontSize: fontStr(96),
        color: '#862736',
      })
      .setOrigin(0, 0);
    this.layer.add(ikon);
    y += 120;

    const header = this.scene.add
      .text(contentX, y, 'Velkommen til Forlagshuset', {
        fontFamily: FONTS.display,
        fontSize: fontStr(58),
        color: '#862736',
        wordWrap: { width: contentW },
      })
      .setOrigin(0, 0);
    this.layer.add(header);
    y += header.height + 32;

    const body = this.scene.add
      .text(
        contentX,
        y,
        'Hva heter du? (Forfatter Bjørn etterlot pulten din tom — og en gammel post-it med Kristines telefonnummer.)',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(28),
          color: '#1a1a1a',
          wordWrap: { width: contentW },
          lineSpacing: 8,
        },
      )
      .setOrigin(0, 0);
    this.layer.add(body);
    y += body.height + 40;

    const inputBgY = y;
    const inputBgH = 96;
    const inputBg = this.scene.add
      .rectangle(contentX, inputBgY, contentW, inputBgH, 0xffffff, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.8);
    this.layer.add(inputBg);
    y += inputBgH + 16;

    this.feilTekst = this.scene.add
      .text(contentX, y, '', {
        fontFamily: FONTS.body,
        fontSize: fontStr(20),
        color: '#862736',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0);
    this.layer.add(this.feilTekst);

    const knappH = 96;
    const knappW = contentW;
    const knappX = contentX;
    const knappY = sheetY + SHEET_HEIGHT - PADDING - knappH;

    this.knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Begynn', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(34),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([this.knappBg, this.knappTxt]);

    this.knappBg.setInteractive({ useHandCursor: true });
    this.knappBg.on('pointerup', () => this.bekreft());

    this.byggInput(sheetX, sheetY, contentW, inputBgY, inputBgH);
    this.oppdaterKnapp();
  }

  close(): void {
    this.fjernDom();
    this.layer?.destroy(true);
    this.layer = undefined;
    this.knappBg = undefined;
    this.knappTxt = undefined;
    this.feilTekst = undefined;
  }

  private byggInput(
    _sheetX: number,
    _sheetY: number,
    bredde: number,
    inputY: number,
    inputH: number,
  ): void {
    const canvas = this.scene.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) return;
    parent.style.position = parent.style.position || 'relative';

    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.zIndex = '60';
    parent.appendChild(wrapper);
    this.domWrapper = wrapper;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Skriv fornavnet ditt …';
    input.maxLength = SPILLER_NAVN_MAX_LENGDE;
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.style.position = 'absolute';
    input.style.boxSizing = 'border-box';
    input.style.padding = '12px 18px';
    input.style.fontFamily = FONTS.body;
    // Responsiv font: skalerer med viewport men aldri under 22px (runde 31)
    input.style.fontSize = 'min(5.2vw, 34px)';
    input.style.lineHeight = '1.3';
    input.style.background = 'transparent';
    input.style.color = '#1a1a1a';
    input.style.border = 'none';
    input.style.outline = 'none';
    input.style.pointerEvents = 'auto';
    input.addEventListener('input', () => this.oppdaterKnapp());
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.bekreft();
      }
    });
    wrapper.appendChild(input);
    this.input = input;

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const contentX = sheetX + PADDING;
    this.plasserInput(contentX, inputY, bredde, inputH);
    window.addEventListener('resize', this.onWindowResize);

    setTimeout(() => input.focus(), 50);
  }

  private onWindowResize = (): void => {
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;
    const contentX = sheetX + PADDING;
    const contentW = SHEET_WIDTH - PADDING * 2;
    const inputY = sheetY + PADDING + 8 + 120 + 58 + 32 + 120 + 40;
    this.plasserInput(contentX, inputY, contentW, 96);
  };

  private plasserInput(
    gameX: number,
    gameY: number,
    gameW: number,
    gameH: number,
  ): void {
    if (!this.input) return;
    const canvas = this.scene.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) return;
    const rect = canvas.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    const skala = rect.width / GAME_WIDTH;
    const offsetX = rect.left - parentRect.left;
    const offsetY = rect.top - parentRect.top;
    this.input.style.left = `${offsetX + gameX * skala}px`;
    this.input.style.top = `${offsetY + gameY * skala}px`;
    this.input.style.width = `${gameW * skala}px`;
    this.input.style.height = `${gameH * skala}px`;
    // Gulvet er nå 22px på alle viewports for lesbarhet (runde 31)
    this.input.style.fontSize = `${Math.max(22, Math.round(34 * skala))}px`;
  }

  private fjernDom(): void {
    window.removeEventListener('resize', this.onWindowResize);
    this.input?.remove();
    this.input = undefined;
    this.domWrapper?.remove();
    this.domWrapper = undefined;
  }

  private gjeldendeNavn(): string {
    return (this.input?.value ?? '').trim();
  }

  private oppdaterKnapp(): void {
    if (!this.knappBg || !this.knappTxt) return;
    const navn = this.gjeldendeNavn();
    const kanBegynne = navn.length >= 1;
    if (kanBegynne) {
      this.knappBg.setFillStyle(COLORS.wine, 1);
      this.knappTxt.setColor('#ffffff');
    } else {
      this.knappBg.setFillStyle(0xcfc6b7, 1);
      this.knappTxt.setColor('#7a7060');
    }
  }

  private bekreft(): void {
    const navn = this.gjeldendeNavn();
    if (navn.length < 1) {
      if (this.feilTekst) this.feilTekst.setText('Skriv minst ett tegn.');
      return;
    }
    const ok = gameState.settSpillerNavn(navn);
    if (!ok) {
      if (this.feilTekst) this.feilTekst.setText('Skriv minst ett tegn.');
      return;
    }
    const lagretNavn = gameState.spillerForfatter.navn;
    const cb = this.onFerdigCb;
    this.onFerdigCb = undefined;
    this.close();
    cb?.(lagretNavn);
  }
}
